/**
 * Persönliche Einstellungen / User Preferences.
 *
 * Persistiert in localStorage (Cache) und im Backend (user.preferences).
 * Wirkt via documentElement.dataset.* + html.dark class.
 */
const STORAGE_KEY = "domivus_user_preferences";

export const DEFAULT_PREFERENCES = {
  // Erscheinungsbild
  theme: "light",                // light | dark | system
  accent_color: "slate",         // slate | teal | blue | amber | rose | emerald | purple
  density: "comfortable",        // comfortable | compact
  font_size: "md",               // sm | md | lg | xl
  sidebar_collapsed: false,
  show_avatars: true,

  // Tabellen
  table_page_size: 25,           // 10 | 25 | 50 | 100
  table_zebra: true,             // wechselseitige Zeilenfarbe

  // Datum / Zeit / Sprache
  date_format: "TT.MM.JJJJ",     // TT.MM.JJJJ | TT. Monat JJJJ
  time_format: "24h",            // 12h | 24h
  week_starts_on: "monday",      // monday | sunday

  // Benachrichtigungen / Toaster
  toast_position: "bottom-right", // top-right | top-left | bottom-right | bottom-left | top-center
  toast_duration: 4000,
  sound_enabled: false,

  // Tastatur / Navigation
  enable_keyboard_shortcuts: true,
  confirm_destructive: true,     // Bestätigung bei Löschen

  // Workflow
  default_landing: "dashboard",  // dashboard | objekte | mietverhaeltnisse
};

export const ACCENT_OPTIONS = [
  { value: "slate",   label: "Schiefergrau (Standard)" },
  { value: "teal",    label: "Mint-Teal" },
  { value: "blue",    label: "Blau" },
  { value: "amber",   label: "Bernstein" },
  { value: "rose",    label: "Rosé" },
  { value: "emerald", label: "Smaragd" },
  { value: "purple",  label: "Lila" },
];

export const FONT_SIZE_OPTIONS = [
  { value: "sm", label: "Klein" },
  { value: "md", label: "Standard" },
  { value: "lg", label: "Groß" },
  { value: "xl", label: "Sehr groß" },
];

export const TOAST_POSITIONS = [
  { value: "top-right", label: "Oben rechts" },
  { value: "top-center", label: "Oben mittig" },
  { value: "top-left", label: "Oben links" },
  { value: "bottom-right", label: "Unten rechts" },
  { value: "bottom-left", label: "Unten links" },
];

export const LANDING_OPTIONS = [
  { value: "dashboard", label: "Dashboard" },
  { value: "objekte", label: "Objekte" },
  { value: "mietverhaeltnisse", label: "Mietverhältnisse" },
  { value: "aufgaben", label: "Aufgaben" },
  { value: "tickets", label: "Tickets" },
];

export function getStoredPreferences() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    return { ...DEFAULT_PREFERENCES, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function applyUserPreferences(prefs) {
  if (!prefs) return;
  const merged = { ...DEFAULT_PREFERENCES, ...prefs };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch (_) { /* ignore */ }

  const root = document.documentElement;

  // --- Theme (light / dark / system)
  let theme = merged.theme;
  if (theme === "system") {
    theme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  root.classList.toggle("dark", theme === "dark");
  root.dataset.theme = theme;

  // --- Akzentfarbe (CSS-Variable)
  root.dataset.accent = merged.accent_color || "slate";

  // --- Dichte
  root.dataset.density = merged.density || "comfortable";

  // --- Schriftgröße
  root.dataset.fontSize = merged.font_size || "md";

  // --- Datum & Zeit
  root.dataset.dateFormat = merged.date_format || "TT.MM.JJJJ";
  root.dataset.timeFormat = merged.time_format || "24h";

  // --- Sidebar
  root.dataset.sidebar = merged.sidebar_collapsed ? "collapsed" : "expanded";

  // --- Toast (sonner liest die data-Attribute beim Mount; wir setzen sie hier)
  root.dataset.toastPosition = merged.toast_position || "bottom-right";
}

export function initPreferences() {
  applyUserPreferences(getStoredPreferences());
}
